import React, { useState } from 'react';
import type { FormikValues } from 'formik';
import { useFormikContext } from 'formik';
import { get, isEmpty } from 'lodash';

import CheckboxFormik from '~/components/CheckboxFormik';
import ArrayMultiselectFieldWithChipsFormik from '~/components/core/ArrayMultiselectFieldWithChipsFormik';
import Grid from '~/components/core/Atomic/Grid/Grid';
import { useDndDragHandle } from '~/components/core/DndOrderedList/DndDragHandleContext';
import useFormikChangeListener from '~/components/core/Formik/FormikChangeListener';
import SwitchFormik from '~/components/core/Formik/SwitchFormik';
import FsTooltip from '~/components/core/FsWrappers/FsTooltip/FsTooltip';
import TextField from '~/components/core/Molecules/Fields/TextField';
import TextFieldFormik from '~/components/TextFieldFormik';

import ReorderDragHandle from '../../components/ReorderDragHandle';
import copies from '../../copies.json';
import HiddenIndication from '../../HiddenIndication/HiddenIndication';
import { buildCategoryLimitOptions, usePolicyCategoryOptions } from '../../PolicyCategoriesOptions';
import BaseConfigurationSection from '../BaseConfigurationSection';
import ConfiguredFieldsSection from '../ConfiguredFieldsSection';

import {
  ADDITIONAL_INFORMATION_MAX_SECTIONS,
  ADDITIONAL_INFORMATION_SECTIONS_PATH,
  CONFIGURED_FIELDS_PATH,
  countActiveSections,
} from './constants';

const TEXTS = copies.additional_information;

type CustomAdditionalInformationSectionProps = {
  index: number;
};

export const sectionHasMandatoryField = (values: FormikValues, sectionId: string): boolean =>
  (get(values, CONFIGURED_FIELDS_PATH, []) as { section?: string; mandatory?: boolean }[]).some(
    (field) => field?.section === sectionId && !!field?.mandatory
  );

const CustomAdditionalInformationSection = ({ index }: CustomAdditionalInformationSectionProps): JSX.Element => {
  const { values, setFieldValue } = useFormikContext<FormikValues>();
  const dragHandleProps = useDndDragHandle();
  const sectionPath = `${ADDITIONAL_INFORMATION_SECTIONS_PATH}[${index}]`;
  const sectionId = get(values, `${sectionPath}.id`);
  const title = get(values, `${sectionPath}.desc`);
  const [isHidden, setIsHidden] = useState(false);

  const policyCategoryOptions = usePolicyCategoryOptions();
  const incidentCategories = get(values, 'configuration.categories', [])?.map(
    (category: { id: string; desc: string }) => ({
      id: category.id,
      value: category.id,
      label: category.desc,
    })
  );
  // Custom sections share the flat key namespace with the built-in ones.
  const categories = buildCategoryLimitOptions(incidentCategories, policyCategoryOptions);

  const hasMandatoryField = sectionHasMandatoryField(values, sectionId);

  // Turning Show *on* is what raises the active count, so only a currently-hidden section can be blocked by
  // the cap. A shown section is never disabled — the admin must always be able to hide one to free a slot.
  const isSectionActive = get(values, `${sectionPath}.active`) !== false;
  // No required-field lock: a section can always be hidden, exactly as the built-in Supplementary
  // Information section can. Hidden means its fields are not part of the FNOL form, so a requirement
  // inside it does not apply - the cap is the only thing that can block the toggle.
  const isShowBlockedByCap =
    !isSectionActive &&
    countActiveSections(get(values, ADDITIONAL_INFORMATION_SECTIONS_PATH)) >= ADDITIONAL_INFORMATION_MAX_SECTIONS;
  // The cap is the only reason Show is ever disabled, so it is the only message this needs.
  const showDisabledTooltip = TEXTS.show_section_at_cap_tooltip.replace(
    '{max}',
    String(ADDITIONAL_INFORMATION_MAX_SECTIONS)
  );

  // Hide the section body when Show is off.
  useFormikChangeListener({
    listenForKeys: [`${sectionPath}.active`],
    onChange: (nextValues: FormikValues) => {
      setIsHidden(!get(nextValues, `${sectionPath}.active`, false));
    },
    runOnFirstRender: true,
  });

  // Force the section Required when any inner field is mandatory (mandatory cascade).
  useFormikChangeListener({
    listenForKeys: [CONFIGURED_FIELDS_PATH],
    onChange: (nextValues: FormikValues) => {
      if (sectionHasMandatoryField(nextValues, sectionId)) {
        setFieldValue(`${sectionPath}.mandatory`, true);
      }
    },
    runOnFirstRender: true,
  });

  return (
    <BaseConfigurationSection
      title={title}
      subtitle={`${TEXTS.key_label}: ${sectionId}`}
      isHidden={isHidden}
      isSection
      // Passing `actions` replaces the default outright, so HiddenIndication is re-declared here. The
      // grip is added only when this card sits in the reorderable list; outside it the header is
      // unchanged. leading-[0] collapses the inline line-box so the grip centres against the chip.
      actions={
        <div className="flex items-center justify-center gap-8 leading-[0]">
          <HiddenIndication isActive={!isHidden} />
          {dragHandleProps ? <ReorderDragHandle dragHandleProps={dragHandleProps} /> : null}
        </div>
      }
    >
      <Grid item xs={6}>
        <TextFieldFormik id={`${sectionPath}.desc`} label={TEXTS.display_name_label} fullWidth />
      </Grid>
      <Grid item xs={6}>
        <TextField id={`${sectionPath}.id`} value={sectionId} label={TEXTS.key_label} fullWidth disabled />
      </Grid>
      <Grid item xs={6}>
        <ArrayMultiselectFieldWithChipsFormik
          id={`${sectionPath}.incident_sub_type_categories`}
          label={TEXTS.limit_to_categories_label}
          options={categories}
          disabled={isEmpty(categories)}
        />
      </Grid>
      <Grid item xs={6} />
      <Grid item xs={12}>
        <Grid container className="flex items-center">
          <Grid item xs={2}>
            {isShowBlockedByCap ? (
              // Same disabled + tooltip treatment the Required checkbox below already gets: the control
              // explains why it cannot move, rather than letting a save fail with no visible error (the yup
              // cap error attaches to the sections array path, which the screen renders nowhere).
              // `bottom-start` not the default `top`: the row directly above the toggle is the
              // "Limit to Categories" select, and a top-placed bubble covers it while hovered.
              <FsTooltip arrow placement="bottom-start" title={showDisabledTooltip}>
                <SwitchFormik id={`${sectionPath}.active`} label={TEXTS.show_label} hideErrorGap disabled />
              </FsTooltip>
            ) : (
              <SwitchFormik id={`${sectionPath}.active`} label={TEXTS.show_label} hideErrorGap />
            )}
          </Grid>
          <Grid item xs={10}>
            {hasMandatoryField ? (
              <FsTooltip arrow title={TEXTS.required_when_field_mandatory_tooltip}>
                <CheckboxFormik id={`${sectionPath}.mandatory`} label={TEXTS.required_label} disabled />
              </FsTooltip>
            ) : (
              // Required on a section with no required field inside it gates on acknowledgement, not on
              // content, so say so here rather than leaving the admin to infer it from the label.
              <FsTooltip arrow title={TEXTS.required_acknowledgement_tooltip}>
                {/* CheckboxFormik's default onChange already does setFieldValue(id, event.target.checked),
                    which for a checkbox is exactly this toggle — no custom handler needed. */}
                <CheckboxFormik id={`${sectionPath}.mandatory`} label={TEXTS.required_label} />
              </FsTooltip>
            )}
          </Grid>
        </Grid>
      </Grid>
      <ConfiguredFieldsSection
        fieldId={CONFIGURED_FIELDS_PATH}
        section={sectionId}
        disabled={isHidden}
        withDndReorder
      />
    </BaseConfigurationSection>
  );
};

export default CustomAdditionalInformationSection;
